import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Radio,
  Row,
  Select,
  Space,
  Typography,
  message,
} from 'antd';
import type { Dayjs } from 'dayjs';
import { DownloadOutlined } from '@ant-design/icons';
import { adminService, PricingVersion, Region } from '../../services/adminService';
import {
  ExportFormat,
  LockedDealsSection,
  UserStatusFilter,
  exportService,
} from '../../services/exportService';

const { Title, Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;

interface GroupOption {
  id: number;
  name: string;
}

type DateRange = [Dayjs | null, Dayjs | null] | null;

const formatDate = (d: Dayjs | null | undefined): string | undefined =>
  d ? d.format('YYYY-MM-DD') : undefined;

interface ExportCardProps {
  title: string;
  description: string;
  filters?: React.ReactNode;
  format: ExportFormat;
  onFormatChange: (format: ExportFormat) => void;
  onDownload: () => Promise<void>;
}

const ExportCard: React.FC<ExportCardProps> = ({
  title,
  description,
  filters,
  format,
  onFormatChange,
  onDownload,
}) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      await onDownload();
      message.success(`${title} export ready`);
    } catch (error: any) {
      message.error(error.message || 'Failed to download export');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      title={<Text strong style={{ color: '#053057' }}>{title}</Text>}
      style={{ height: '100%', borderRadius: 8 }}
      styles={{ header: { background: '#F4F3F0' } }}
    >
      <Paragraph type="secondary" style={{ minHeight: 44 }}>
        {description}
      </Paragraph>

      {filters && (
        <Collapse
          ghost
          size="small"
          style={{ marginBottom: 12 }}
          items={[
            {
              key: 'filters',
              label: <Text type="secondary">Optional filters</Text>,
              children: filters,
            },
          ]}
        />
      )}

      <div style={{ marginBottom: 16 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
          Format
        </Text>
        <Radio.Group
          value={format}
          onChange={e => onFormatChange(e.target.value)}
          optionType="button"
          buttonStyle="solid"
          options={[
            { label: 'Excel (.xlsx)', value: 'xlsx' },
            { label: 'CSV', value: 'csv' },
          ]}
        />
      </div>

      <Button
        type="primary"
        icon={<DownloadOutlined />}
        loading={loading}
        onClick={handleClick}
        block
        style={{ background: '#053057', borderColor: '#053057' }}
      >
        Download
      </Button>
    </Card>
  );
};

const DataExportsTab: React.FC = () => {
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [pricingVersions, setPricingVersions] = useState<PricingVersion[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);

  // Users filters
  const [usersFormat, setUsersFormat] = useState<ExportFormat>('xlsx');
  const [usersStatus, setUsersStatus] = useState<UserStatusFilter>('active');
  const [usersGroupIds, setUsersGroupIds] = useState<number[]>([]);

  // Locked Deals filters (merged: net prices + resource prices)
  const [lockedFormat, setLockedFormat] = useState<ExportFormat>('xlsx');
  const [lockedSections, setLockedSections] = useState<LockedDealsSection>('both');
  const [lockedRange, setLockedRange] = useState<DateRange>(null);
  const [lockedRegionIds, setLockedRegionIds] = useState<number[]>([]);

  // Pricing Details filters
  const [pricingFormat, setPricingFormat] = useState<ExportFormat>('xlsx');
  const [pricingVersionId, setPricingVersionId] = useState<number | undefined>(undefined);

  useEffect(() => {
    (async () => {
      try {
        const data = await adminService.getActiveGroups();
        setGroups(data);
      } catch {
        // Non-blocking — group filter is optional
      }
    })();
    (async () => {
      try {
        const data = await adminService.getPricingVersions();
        setPricingVersions(data);
        // Default to the latest version (first, newest-first from the API)
        if (data.length) {
          setPricingVersionId(data[0].id);
        }
      } catch {
        // Non-blocking — defaults to latest version server-side
      }
    })();
    (async () => {
      try {
        const data = await adminService.getRegions();
        setRegions(data);
      } catch {
        // Non-blocking — region filter is optional
      }
    })();
  }, []);

  return (
    <div>
      <Title level={4} style={{ marginTop: 0 }}>
        Data Exports
      </Title>
      <Paragraph type="secondary">
        Download operational data as Excel or CSV. Filters are optional &mdash; leave
        them blank to export the full dataset.
      </Paragraph>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12} lg={12}>
          <ExportCard
            title="Users"
            description="All users with their assigned group, filterable by status and group."
            format={usersFormat}
            onFormatChange={setUsersFormat}
            filters={
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                    Status
                  </Text>
                  <Radio.Group
                    value={usersStatus}
                    onChange={e => setUsersStatus(e.target.value)}
                    options={[
                      { label: 'Active', value: 'active' },
                      { label: 'Inactive', value: 'inactive' },
                      { label: 'All', value: 'all' },
                    ]}
                  />
                </div>
                <div>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                    Groups
                  </Text>
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="All groups"
                    value={usersGroupIds}
                    onChange={setUsersGroupIds}
                    style={{ width: '100%' }}
                    options={groups.map(g => ({ label: g.name, value: g.id }))}
                  />
                </div>
              </Space>
            }
            onDownload={() =>
              exportService.exportUsers({
                format: usersFormat,
                status: usersStatus,
                group_ids: usersGroupIds.length ? usersGroupIds : undefined,
              })
            }
          />
        </Col>

        <Col xs={24} md={12} lg={12}>
          <ExportCard
            title="Locked Deals"
            description="Net project totals and per-resource pricing for every locked deal version. Pick one or both — Excel returns multiple sheets; CSV with both returns a .zip."
            format={lockedFormat}
            onFormatChange={setLockedFormat}
            filters={
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                    Include
                  </Text>
                  <Radio.Group
                    value={lockedSections}
                    onChange={e => setLockedSections(e.target.value)}
                    optionType="button"
                    options={[
                      { label: 'Both', value: 'both' },
                      { label: 'Net prices', value: 'net' },
                      { label: 'Resource details', value: 'resources' },
                    ]}
                  />
                </div>
                <div>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                    Deal locked on
                  </Text>
                  <RangePicker
                    allowEmpty={[true, true]}
                    value={lockedRange as any}
                    onChange={vals => setLockedRange(vals as DateRange)}
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD"
                  />
                </div>
                <div>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                    Region (applies to resource details)
                  </Text>
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="All regions"
                    value={lockedRegionIds}
                    onChange={setLockedRegionIds}
                    style={{ width: '100%' }}
                    options={regions.map(r => ({ label: r.region_code, value: r.region_id }))}
                  />
                </div>
              </Space>
            }
            onDownload={() =>
              exportService.exportLockedDeals({
                format: lockedFormat,
                sections: lockedSections,
                locked_from: formatDate(lockedRange?.[0]),
                locked_to: formatDate(lockedRange?.[1]),
                region_ids: lockedRegionIds.length ? lockedRegionIds : undefined,
              })
            }
          />
        </Col>

        <Col xs={24} md={12} lg={12}>
          <ExportCard
            title="Pricing Details"
            description="Base pricing rates (cost/hour by currency, burden, target margin) for the selected base price version. Defaults to the latest version."
            format={pricingFormat}
            onFormatChange={setPricingFormat}
            filters={
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                    Base price version
                  </Text>
                  <Select
                    showSearch
                    placeholder="Latest version"
                    value={pricingVersionId}
                    onChange={setPricingVersionId}
                    style={{ width: '100%' }}
                    optionFilterProp="label"
                    options={pricingVersions.map((v, idx) => ({
                      label: idx === 0 ? `${v.name} (latest)` : v.name,
                      value: v.id,
                    }))}
                  />
                </div>
              </Space>
            }
            onDownload={() =>
              exportService.exportPricingDetails({
                format: pricingFormat,
                version_id: pricingVersionId,
              })
            }
          />
        </Col>
      </Row>
    </div>
  );
};

export default DataExportsTab;
